import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'

import { COL_MACHINES } from '../../../config/collections'
import { PERMISSION_KEYS } from '../../../config/permissions'
import { pocketbaseErrorMessage } from '../../../lib/pocketbaseErrorMessage'
import {
  availabilityPercent,
  formatHours,
  formatMachineOutput,
  machineTypeLabel,
} from '../../../lib/machines'
import { matchesListSearch } from '../../../lib/listSearch'
import { pb } from '../../../pocketbase'
import { useAuth } from '../../../context/AuthContext'
import type { MachineRecord } from '../../../types/records'
import { PageHeader, PageShell, Alert, ButtonLink, SearchField } from '../../../components/ui'
import {
  btn,
  cn,
  tableCellClass,
  tableClass,
  tableHeadClass,
  tableLastRowClass,
  tableRowHoverClass,
  tableWrapClass,
  toolbarClass,
} from '../../../lib/ui'

export function MachinesPage() {
  const { can } = useAuth()
  const [items, setItems] = useState<MachineRecord[]>([])
  const [searchQuery, setSearchQuery] = useState('')
  const [err, setErr] = useState('')
  const [loading, setLoading] = useState(true)

  const load = useCallback(async () => {
    setErr('')
    setLoading(true)
    try {
      const list = await pb.collection(COL_MACHINES).getFullList<MachineRecord>({
        sort: 'type,sort,number',
      })
      setItems(list)
    } catch (e: unknown) {
      setErr(pocketbaseErrorMessage(e, 'Ошибка загрузки'))
      setItems([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  async function removeRow(id: string) {
    if (!window.confirm('Удалить станок?')) return
    try {
      await pb.collection(COL_MACHINES).delete(id)
      await load()
    } catch (e: unknown) {
      alert(pocketbaseErrorMessage(e, 'Ошибка удаления'))
    }
  }

  const displayedItems = useMemo(() => {
    if (!searchQuery.trim()) return items
    return items.filter((row) =>
      matchesListSearch(
        searchQuery,
        machineTypeLabel(row.type),
        String(row.number),
        formatMachineOutput(row),
        String(row.working_hours_per_day),
        String(row.maintenance_hours_per_day),
      ),
    )
  }, [items, searchQuery])

  return (
    <PageShell>
      <PageHeader
        title="Станки"
        lead="Парк производственных станков: тип линии, номер, производительность, рабочее время и техобслуживание."
      />
      {err && <Alert kind="err">{err}</Alert>}
      <div className={toolbarClass}>
        {can(PERMISSION_KEYS.MACHINES_WRITE) && (
          <ButtonLink to="/admin/administration/machines/new" variant="primary">
            Добавить станок
          </ButtonLink>
        )}
        <button type="button" className={btn()} onClick={() => void load()} disabled={loading}>
          Обновить
        </button>
        <div className="flex min-w-40 flex-1 justify-end">
          <SearchField
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Поиск по типу, №, параметрам…"
            aria-label="Поиск станков"
          />
        </div>
      </div>
      <div className={cn(tableWrapClass, tableLastRowClass)}>
        <table className={tableClass}>
          <thead>
            <tr>
              <th className={tableHeadClass}>Тип</th>
              <th className={tableHeadClass}>№</th>
              <th className={tableHeadClass}>Производительность</th>
              <th className={tableHeadClass}>Работа, ч/сут</th>
              <th className={tableHeadClass}>ТО, ч/сут</th>
              <th className={tableHeadClass}>Доступность</th>
              <th className={tableHeadClass} />
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr className={tableRowHoverClass}>
                <td className={tableCellClass} colSpan={7}>
                  <em>Загрузка…</em>
                </td>
              </tr>
            )}
            {!loading && displayedItems.length === 0 && (
              <tr className={tableRowHoverClass}>
                <td className={tableCellClass} colSpan={7}>
                  <em>
                    {items.length === 0
                      ? 'Нет станков — добавьте первый или дождитесь применения миграции.'
                      : 'Ничего не найдено'}
                  </em>
                </td>
              </tr>
            )}
            {!loading &&
              displayedItems.map((row) => (
                <tr key={row.id} className={tableRowHoverClass}>
                  <td className={tableCellClass}>{machineTypeLabel(row.type)}</td>
                  <td className={tableCellClass}>№{row.number}</td>
                  <td className={tableCellClass}>{formatMachineOutput(row)}</td>
                  <td className={tableCellClass}>{formatHours(row.working_hours_per_day)}</td>
                  <td className={tableCellClass}>{formatHours(row.maintenance_hours_per_day)}</td>
                  <td className={tableCellClass}>{availabilityPercent(row)}%</td>
                  <td className={cn(tableCellClass, 'whitespace-nowrap')}>
                    {can(PERMISSION_KEYS.MACHINES_WRITE) && (
                      <Link to={`/admin/administration/machines/${row.id}`} className={btn('ghost')}>
                        Изменить
                      </Link>
                    )}
                    {can(PERMISSION_KEYS.MACHINES_DELETE) && (
                      <button
                        type="button"
                        className={cn(btn('danger'), 'ml-1')}
                        onClick={() => void removeRow(row.id)}
                      >
                        Удалить
                      </button>
                    )}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </PageShell>
  )
}
